import { useEffect, useRef } from "react";
import { useScroll, useMotionValueEvent } from "framer-motion";
import { cn } from "@/lib/utils";

const reducedMotion = () =>
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const ParticleMorph = ({ words = ["IDEA", "FINAL KEY"], className, testid }) => {
    const wrapRef = useRef(null);
    const canvasRef = useRef(null);
    const progressRef = useRef(0);
    const { scrollYProgress } = useScroll({ target: wrapRef, offset: ["start end", "end start"] });
    useMotionValueEvent(scrollYProgress, "change", (v) => { progressRef.current = v; });

    useEffect(() => {
        if (reducedMotion()) return undefined;
        const canvas = canvasRef.current;
        const wrap = wrapRef.current;
        if (!canvas || !wrap) return undefined;
        const ctx = canvas.getContext("2d");
        const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
        let raf;
        let particles = [];

        const sample = (text, w, h) => {
            const off = document.createElement("canvas");
            off.width = w; off.height = h;
            const octx = off.getContext("2d");
            octx.fillStyle = "#fff";
            const size = Math.min(w / (text.length * 0.62), h * 0.55);
            octx.font = `900 ${size}px Outfit, sans-serif`;
            octx.textAlign = "center";
            octx.textBaseline = "middle";
            octx.fillText(text, w / 2, h / 2);
            const data = octx.getImageData(0, 0, w, h).data;
            const pts = [];
            for (let y = 0; y < h; y += 6) {
                for (let x = 0; x < w; x += 6) {
                    if (data[(y * w + x) * 4 + 3] > 120) pts.push([x, y]);
                }
            }
            return pts;
        };

        const setup = () => {
            const w = wrap.clientWidth;
            const h = wrap.clientHeight;
            canvas.width = w * dpr;
            canvas.height = h * dpr;
            canvas.style.width = `${w}px`;
            canvas.style.height = `${h}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            const A = sample(words[0], w, h);
            const B = sample(words[1], w, h);
            if (!A.length || !B.length) return;
            const n = Math.min(A.length, B.length, 750);
            particles = Array.from({ length: n }, (_, i) => ({
                a: A[Math.floor((i * A.length) / n)],
                b: B[Math.floor((i * B.length) / n)],
                x: Math.random() * w,
                y: Math.random() * h,
                c: Math.random() < 0.85 ? "96,158,255" : "255,87,34",
                r: Math.random() * 1.5 + 0.8,
            }));
        };

        const lerp = (a, b, t) => a + (b - a) * t;
        const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

        const draw = () => {
            const w = wrap.clientWidth;
            const h = wrap.clientHeight;
            ctx.clearRect(0, 0, w, h);
            const p = progressRef.current;
            const gather = ease(Math.min(1, p / 0.3));
            const morph = ease(Math.max(0, Math.min(1, (p - 0.55) / 0.3)));
            for (const pt of particles) {
                const tx = lerp(pt.a[0], pt.b[0], morph);
                const ty = lerp(pt.a[1], pt.b[1], morph);
                pt.x = lerp(pt.x, tx, 0.05 + gather * 0.07);
                pt.y = lerp(pt.y, ty, 0.05 + gather * 0.07);
                ctx.fillStyle = `rgba(${pt.c},${0.2 + gather * 0.65})`;
                ctx.beginPath();
                ctx.arc(pt.x, pt.y, pt.r, 0, 6.283);
                ctx.fill();
            }
            raf = requestAnimationFrame(draw);
        };

        setup();
        draw();
        const onResize = () => setup();
        window.addEventListener("resize", onResize);
        return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", onResize); };
    }, [words]);

    return (
        <div ref={wrapRef} className={cn("relative", className)} data-testid={testid} aria-hidden>
            <canvas ref={canvasRef} className="block" />
        </div>
    );
};
